import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import LinearProgress from '@material-ui/core/LinearProgress'
import Switch from '@material-ui/core/Switch'
import Typography from '@material-ui/core/Typography'
import EventBusyIcon from 'components/material-symbol/icons/EventBusy'
import PersonAddIcon from 'components/material-symbol/icons/PersonAdd'
import AccessTimeIcon from 'components/material-symbol/icons/AccessTime'
import TrendingDownIcon from 'components/material-symbol/icons/TrendingDown'
import OpenInNewOutlinedIcon from 'components/material-symbol/icons/OpenInNew'
import Error from 'components/error'
import { PageHeader } from 'components/page-header'
import UpgradeSuccessModal from 'components/upgrade-success-modal'
import { useUpgradeFlow } from 'lib/hooks/use-upgrade-flow'
import { amplitudeEvent } from 'lib/amplitude'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import useSettingsStyles, { SettingsFormControlLabel } from '../settings.styles'
import { SettingsSection, SettingsToggleRow } from '../settings-components'
import {
  useFollowUpAutomations,
  useUpdateOutreachTemplate,
  useUpsertOutreachRule,
} from './api-hooks'
import ApprovalDialog from './approval-dialog'
import EditRuleDialog from './edit-rule-dialog'
import ResultsSection from './results-section'
import { PAGE_NAME, RULE_COPY, describeRule, lowerFirst } from './constants'
import useStyles from './styles'

const RULE_ICONS = {
  CANCELED: <EventBusyIcon />,
  NEW: <PersonAddIcon />,
  DUE: <AccessTimeIcon />,
  LAPSED: <TrendingDownIcon />,
}

export function FollowUpAutomationsView({
  automations,
  editableTriggers,
  crumbs,
  savingTrigger,
  upgradeLoading,
  onToggle,
  onEdit,
  onUpgrade,
}) {
  const classes = useStyles()
  const settingsStyles = useSettingsStyles()
  const {
    available,
    upgradeMessage,
    dailyCap,
    sentToday,
    sendSchedule,
    holdoutPercent,
    results,
    rules,
  } = automations

  return (
    <>
      <PageHeader title="Follow-up Automations" crumbs={crumbs} />
      <Box className={settingsStyles.contentContainer}>
        {!available ? (
          <Box className={classes.upgradeBanner}>
            <Box className={classes.upgradeCopy}>
              <Typography className={classes.upgradeTitle}>
                Let PGA Coach follow up for you
              </Typography>
              <Typography className={classes.upgradeCaption}>
                {upgradeMessage} Turn one on and your message goes out
                automatically when a student cancels, is new, or stops booking.
                You can still edit your messages and send them yourself.
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              className={classes.pillButton}
              onClick={onUpgrade}
              disabled={upgradeLoading}
              startIcon={<OpenInNewOutlinedIcon />}
            >
              Upgrade Now
            </Button>
          </Box>
        ) : null}

        <SettingsSection
          eyebrow="Automations"
          subtitle={`You approve who gets each message before it turns on. Sends ${lowerFirst(
            sendSchedule,
          )}, up to ${dailyCap} a day across all automations.`}
          classes={settingsStyles}
        >
          {rules
            .filter(rule => RULE_COPY[rule.trigger])
            .map(rule => {
              const { title } = RULE_COPY[rule.trigger]
              return (
                <SettingsToggleRow
                  key={rule.trigger}
                  icon={RULE_ICONS[rule.trigger]}
                  title={title}
                  description={describeRule(rule.trigger, rule.delayDays)}
                  badge={rule.active ? 'On' : 'Off'}
                  action={
                    <Box className={classes.ruleActions}>
                      {editableTriggers.includes(rule.trigger) ? (
                        <Button
                          color="primary"
                          className={classes.editButton}
                          onClick={() => onEdit(rule)}
                          aria-label={`Edit ${title}`}
                        >
                          Edit
                        </Button>
                      ) : null}
                      <SettingsFormControlLabel
                        label=" "
                        control={
                          <Switch
                            checked={rule.active}
                            disabled={
                              (!available && !rule.active) ||
                              savingTrigger === rule.trigger
                            }
                            onChange={event =>
                              onToggle(rule, event.target.checked)
                            }
                            color="primary"
                            inputProps={{ 'aria-label': `Automate ${title}` }}
                          />
                        }
                      />
                    </Box>
                  }
                  classes={settingsStyles}
                />
              )
            })}
        </SettingsSection>

        <ResultsSection results={results} holdoutPercent={holdoutPercent} />

        <Typography component="p" className={classes.footerNote}>
          {sentToday >= dailyCap
            ? `Today's limit of ${dailyCap} automated follow-ups is reached. More go out on the next scheduled send.`
            : `${sentToday} of ${dailyCap} automated follow-ups sent today.`}
        </Typography>
      </Box>
    </>
  )
}

FollowUpAutomationsView.propTypes = {
  editableTriggers: PropTypes.arrayOf(PropTypes.string).isRequired,
  automations: PropTypes.shape({
    available: PropTypes.bool.isRequired,
    upgradeMessage: PropTypes.string.isRequired,
    dailyCap: PropTypes.number.isRequired,
    sentToday: PropTypes.number.isRequired,
    sendSchedule: PropTypes.string.isRequired,
    holdoutPercent: PropTypes.number.isRequired,
    results: PropTypes.object.isRequired,
    rules: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  crumbs: PropTypes.arrayOf(PropTypes.object),
  savingTrigger: PropTypes.string,
  upgradeLoading: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onUpgrade: PropTypes.func.isRequired,
}

export default function FollowUpAutomations() {
  const { path } = useRouteMatch()
  const { enqueueSnackbar } = useSnackbar()
  const snackbarSuccess = useSnackbarSuccess(enqueueSnackbar)
  const snackbarError = useSnackbarError(enqueueSnackbar)
  const { data, loading, error, refetch } = useFollowUpAutomations()
  const [upsertRule] = useUpsertOutreachRule()
  const [updateTemplate] = useUpdateOutreachTemplate()
  const {
    showSuccessModal,
    upgradeLoading,
    handleUpgradeClick,
    handleDismiss,
    trackPromptShown,
  } = useUpgradeFlow(PAGE_NAME, { trackPromptShown: false })
  const [approving, setApproving] = useState(null)
  const [editing, setEditing] = useState(null)
  const [savingTrigger, setSavingTrigger] = useState(null)
  const [dialogError, setDialogError] = useState(null)
  const available = data?.outreachAutomations?.available

  useEffect(() => {
    if (available === false) trackPromptShown()
  }, [available, trackPromptShown])

  if (loading && !data) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />

  const automations = data?.outreachAutomations
  if (!automations) return <Error statusCode={404} />

  const crumbs = [
    {
      label: 'Settings',
      to: path
        .split('/')
        .slice(0, 3)
        .join('/'),
    },
  ]

  const closeDialogs = () => {
    setApproving(null)
    setEditing(null)
    setDialogError(null)
  }

  const withSaving = async (trigger, save) => {
    setSavingTrigger(trigger)
    try {
      await save()
    } finally {
      setSavingTrigger(null)
    }
  }

  const saveRule = async (trigger, variables) => {
    const { data: result } = await upsertRule({
      variables: { trigger, ...variables },
    })
    return result.upsertOutreachRule
  }

  const handleToggle = async (rule, checked) => {
    if (checked) {
      setDialogError(null)
      setApproving(rule)
      return
    }
    await withSaving(rule.trigger, async () => {
      try {
        const response = await saveRule(rule.trigger, { enabled: false })
        if (!response.success) return snackbarError(response.message)
        amplitudeEvent('follow-up-automation-disabled', {
          trigger: rule.trigger,
        })
        snackbarSuccess(response.message)
        await refetch()
      } catch (e) {
        snackbarError(e.message)
      }
    })
  }

  const handleApprove = () =>
    withSaving(approving.trigger, async () => {
      try {
        const response = await saveRule(approving.trigger, {
          enabled: true,
          delayDays: approving.delayDays,
        })
        if (!response.success) return setDialogError(response.message)
        amplitudeEvent('follow-up-automation-enabled', {
          trigger: approving.trigger,
          delayDays: approving.delayDays,
        })
        snackbarSuccess(
          approving.previousDelayDays == null
            ? response.message
            : 'New timing saved',
        )
        closeDialogs()
        await refetch()
      } catch (e) {
        setDialogError(e.message)
      }
    })

  const handleCancelApproval = () => {
    amplitudeEvent('follow-up-automation-preview-canceled', {
      trigger: approving.trigger,
    })
    if (approving.previousDelayDays != null) {
      enqueueSnackbar(
        `${
          approving.messageSaved ? 'Message saved. ' : ''
        }Timing not changed. ${describeRule(
          approving.trigger,
          approving.previousDelayDays,
        )}`,
        { variant: 'info' },
      )
    }
    closeDialogs()
  }

  const templateFor = trigger =>
    data.outreachTemplates.find(template => template.trigger === trigger)

  const handleSave = ({ delayDays, body }) => {
    const { trigger } = editing
    const template = templateFor(trigger)
    return withSaving(trigger, async () => {
      try {
        if (body !== template.body) {
          const { data: result } = await updateTemplate({
            variables: {
              trigger,
              body: body === template.defaultBody ? '' : body,
            },
          })
          if (!result.updateOutreachTemplate.success) {
            return setDialogError(result.updateOutreachTemplate.message)
          }
        }
        const delayChanged = delayDays !== editing.delayDays
        if (delayChanged && !editing.active) {
          const response = await saveRule(trigger, { delayDays })
          if (!response.success) {
            refetch()
            return setDialogError(response.message)
          }
        }
        amplitudeEvent('follow-up-automation-saved', {
          trigger,
          messageChanged: body !== template.body,
          delayChanged: delayChanged && !editing.active,
        })
        if (delayChanged && editing.active) {
          setEditing(null)
          setDialogError(null)
          setApproving({
            ...editing,
            delayDays,
            previousDelayDays: editing.delayDays,
            messageSaved: body !== template.body,
          })
        } else {
          snackbarSuccess('Automation saved')
          closeDialogs()
        }
        await refetch()
      } catch (e) {
        setDialogError(e.message)
      }
    })
  }

  return (
    <>
      <FollowUpAutomationsView
        automations={automations}
        editableTriggers={data.outreachTemplates.map(({ trigger }) => trigger)}
        crumbs={crumbs}
        savingTrigger={savingTrigger}
        upgradeLoading={upgradeLoading}
        onToggle={handleToggle}
        onEdit={rule => {
          setDialogError(null)
          setEditing(rule)
        }}
        onUpgrade={handleUpgradeClick}
      />
      {approving ? (
        <ApprovalDialog
          rule={approving}
          sendSchedule={automations.sendSchedule}
          holdoutPercent={automations.holdoutPercent}
          saving={savingTrigger === approving.trigger}
          error={dialogError}
          onCancel={handleCancelApproval}
          onConfirm={handleApprove}
        />
      ) : null}
      {editing ? (
        <EditRuleDialog
          rule={editing}
          template={templateFor(editing.trigger)}
          saving={savingTrigger === editing.trigger}
          error={dialogError}
          onCancel={closeDialogs}
          onSave={handleSave}
        />
      ) : null}
      <UpgradeSuccessModal isOpen={showSuccessModal} onClose={handleDismiss} />
    </>
  )
}
